import React from "react";

import { Modal } from "ui/components/Modal";
import ModalHeader from "ui/components/Modal/ModalHeader";
import ModalBody from "ui/components/Modal/ModalBody";
import ModalFooter from "ui/components/Modal/ModalFooter";
import { Button } from "ui/components/Button/Button";
import { ResidualValueResponse } from "services/api/catalog/cvh/types";

interface ResidualValueResultModalProps {
  result: ResidualValueResponse | null;
  onClose: () => void;
}

const formatCurrency = (value: number | null | undefined) => {
  if (value == null) return "-";
  return `${value.toLocaleString("hr-HR", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €`;
};

const formatNumber = (value: number | null | undefined) => {
  if (value == null) return "-";
  return value.toLocaleString("hr-HR");
};

const ResidualValueResultModal: React.FC<ResidualValueResultModalProps> = ({
  result,
  onClose,
}) => {
  return (
    <Modal
      show={!!result}
      position="centered"
      size="medium"
      onHide={onClose}
    >
      <ModalHeader
        title="Rezultat izračuna"
        onHide={onClose}
      />
      <ModalBody>
        {result && (
          result.calculationError ? (
            <div className="p-6">
              <div className="bg-red-50 border border-red-200 rounded p-4">
                <p className="text-red-800 font-medium">Greška pri izračunu</p>
                <p className="text-red-600 text-sm mt-1">{result.errorMessage}</p>
              </div>
            </div>
          ) : (
            <div className="p-6 space-y-6 overflow-y-auto max-h-[70vh]">
              <div className="flex items-center gap-3">
                {result.isUsedVehicle && (
                  <span className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-full text-xs font-medium bg-amber-100 text-amber-800">
                    <span className="w-2 h-2 rounded-full bg-amber-500" />
                    Rabljeno vozilo
                  </span>
                )}
                {result.isManualPriceEntry && (
                  <span className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                    <span className="w-2 h-2 rounded-full bg-blue-500" />
                    Ručni unos cijene
                  </span>
                )}
              </div>

              <div className="bg-slate-50 border border-slate-200 rounded p-4">
                <h3 className="font-medium text-slate-900 mb-3">Podaci o vozilu</h3>
                <div className="grid grid-cols-2 gap-x-6 gap-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-gray-500">Stanje putomjera:</span>
                    <span className="font-medium">{result.odometerReading != null ? `${formatNumber(result.odometerReading)} km` : "-"}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-500">Putomjer na početku:</span>
                    <span className="font-medium">{result.odometerReadingAtStart != null ? `${formatNumber(result.odometerReadingAtStart)} km` : "-"}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-500">Putomjer na kraju:</span>
                    <span className="font-medium">{result.odometerReadingAtEnd != null ? `${formatNumber(result.odometerReadingAtEnd)} km` : "-"}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-500">Starost na početku:</span>
                    <span className="font-medium">{result.vehicleAgeAtStart != null ? `${formatNumber(result.vehicleAgeAtStart)} mj.` : "-"}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-500">Starost na kraju:</span>
                    <span className="font-medium">{result.vehicleAgeAtEnd != null ? `${formatNumber(result.vehicleAgeAtEnd)} mj.` : "-"}</span>
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-6">
                <div className="bg-blue-50 border border-blue-200 rounded p-4">
                  <h3 className="font-medium text-blue-900 mb-3">Otkupna vrijednost</h3>
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between">
                      <span className="text-gray-600">Osnovna cijena:</span>
                      <span className="font-medium">{formatCurrency(result.buybackValueBasePrice)}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Carina:</span>
                      <span className="font-medium">{formatCurrency(result.buybackValueCustomsDuty)}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">PDV:</span>
                      <span className="font-medium">{formatCurrency(result.buybackValueVAT)}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">PPMV vrijednosna:</span>
                      <span className="font-medium">{formatCurrency(result.buybackValuePPMVValueComponent)}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">PPMV ekološka:</span>
                      <span className="font-medium">{formatCurrency(result.buybackValuePPMVEcologicalComponent)}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">PPMV ukupno:</span>
                      <span className="font-medium">{formatCurrency(result.buybackValuePPMVTotal)}</span>
                    </div>
                    <div className="flex justify-between border-t border-blue-300 pt-2 mt-2">
                      <span className="text-blue-900 font-medium">Ukupno:</span>
                      <span className="text-blue-900 font-bold">{formatCurrency(result.buybackValueTotal)}</span>
                    </div>
                  </div>
                </div>

                <div className="bg-green-50 border border-green-200 rounded p-4">
                  <h3 className="font-medium text-green-900 mb-3">Prodajna vrijednost</h3>
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between">
                      <span className="text-gray-600">Osnovna cijena:</span>
                      <span className="font-medium">{formatCurrency(result.sellingValueBasePrice)}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Carina:</span>
                      <span className="font-medium">{formatCurrency(result.sellingValueCustomsDuty)}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">PDV:</span>
                      <span className="font-medium">{formatCurrency(result.sellingValueVAT)}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">PPMV vrijednosna:</span>
                      <span className="font-medium">{formatCurrency(result.sellingValuePPMVValueComponent)}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">PPMV ekološka:</span>
                      <span className="font-medium">{formatCurrency(result.sellingValuePPMVEcologicalComponent)}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">PPMV ukupno:</span>
                      <span className="font-medium">{formatCurrency(result.sellingValuePPMVTotal)}</span>
                    </div>
                    <div className="flex justify-between border-t border-green-300 pt-2 mt-2">
                      <span className="text-green-900 font-medium">Ukupno:</span>
                      <span className="text-green-900 font-bold">{formatCurrency(result.sellingValueTotal)}</span>
                    </div>
                  </div>
                </div>
              </div>

            </div>
          )
        )}
      </ModalBody>

      <ModalFooter>
        <div className="flex justify-end px-4 py-3">
          <Button variant="outlined" onClick={onClose}>
            Zatvori
          </Button>
        </div>
      </ModalFooter>
    </Modal>
  );
};

export default ResidualValueResultModal;
